import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Play, AudioLines } from 'lucide-react';
import { useMediaPlayer } from '../project/hooks/useMediaPlayer';
import { MediaControls } from '../project/components/MediaControls';
import type { ResourceContent } from './ResourcePage';

interface ResourceViewerProps {
  content: ResourceContent;
  fileExt: string;
  fullscreen: boolean;
  onCloseFullscreen: () => void;
}

// ── Lightbox ─────────────────────────────────────────────────────────

function Lightbox({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-8"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      {children}
      <button
        onClick={onClose}
        className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
        title="Close"
      >
        <X className="h-5 w-5" />
      </button>
    </div>,
    document.body,
  );
}

// ── Native Video Player ─────────────────────────────────────────────

function ResourceVideoPlayer({ content }: { content: ResourceContent }) {
  const player = useMediaPlayer();

  useEffect(() => {
    player.load();
  }, [content.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mimeType = content.mimeType === 'video/quicktime' ? 'video/mp4' : content.mimeType;

  return (
    <div
      ref={(el) => { player.containerRef.current = el; }}
      className="group/player relative flex h-full w-full items-center justify-center bg-black"
    >
      <video
        ref={player.ref as React.RefObject<HTMLVideoElement>}
        className="h-full w-full object-contain"
        preload="auto"
        crossOrigin="anonymous"
        onClick={player.togglePlay}
      >
        {content.signedUrl && <source src={content.signedUrl} type={mimeType} />}
      </video>

      {/* Play icon overlay */}
      {!player.isPlaying && (
        <div className="pointer-events-none absolute inset-0 z-1 flex items-center justify-center opacity-80 transition-opacity group-hover/player:opacity-0">
          <div className="flex size-16 items-center justify-center rounded-full bg-black/50">
            <Play className="ml-1 size-8 text-white" />
          </div>
        </div>
      )}

      <MediaControls
        currentTime={player.currentTime}
        duration={player.duration}
        isPlaying={player.isPlaying}
        isFullscreen={player.isFullscreen}
        playbackRate={player.playbackRate}
        onTogglePlay={player.togglePlay}
        onSeek={player.seek}
        onSetRate={player.setRate}
        onToggleFullscreen={player.toggleFullscreen}
        mediaRef={player.ref}
      />
    </div>
  );
}

// ── Native Audio Player ─────────────────────────────────────────────

function ResourceAudioPlayer({ content }: { content: ResourceContent }) {
  const player = useMediaPlayer();

  useEffect(() => {
    player.load();
  }, [content.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="group/player relative flex h-full w-full flex-col overflow-hidden">
      <audio
        ref={player.ref as React.RefObject<HTMLAudioElement>}
        preload="auto"
        crossOrigin="anonymous"
      >
        {content.signedUrl && <source src={content.signedUrl} type={content.mimeType} />}
      </audio>

      {/* Visual area */}
      <div
        className="flex flex-1 cursor-pointer items-center justify-center bg-blue-600"
        onClick={player.togglePlay}
      >
        <AudioLines
          className={`size-1/3 max-h-32 text-white transition-transform ${player.isPlaying ? 'animate-pulse' : ''}`}
        />
      </div>

      {/* Controls at bottom */}
      <div className="relative h-14 shrink-0 bg-gray-900">
        <div className="absolute inset-0 flex flex-col justify-center">
          <MediaControls
            currentTime={player.currentTime}
            duration={player.duration}
            isPlaying={player.isPlaying}
            isFullscreen={false}
            playbackRate={player.playbackRate}
            onTogglePlay={player.togglePlay}
            onSeek={player.seek}
            onSetRate={player.setRate}
            onToggleFullscreen={() => {}}
            hideFullscreen
            mediaRef={player.ref}
          />
        </div>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────

export default function ResourceViewer({
  content,
  fileExt,
  fullscreen,
  onCloseFullscreen,
}: ResourceViewerProps) {
  // Track which URL has loaded or failed instead of resetting flags in an effect. The effect ran after mount and
  // undid onLoad for images served from the browser cache, leaving the image hidden behind the spinner forever.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const imageError = failedSrc === content.signedUrl;
  const loading = loadedSrc !== content.signedUrl && !imageError;

  // ── Video ──────────────────────────────────────────────────────────
  if (content.type === 'video') {
    return <ResourceVideoPlayer content={content} />;
  }

  // ── Audio ──────────────────────────────────────────────────────────
  if (content.type === 'audio') {
    return <ResourceAudioPlayer content={content} />;
  }

  // ── Image ──────────────────────────────────────────────────────────
  if (content.type === 'image') {
    if (imageError) {
      return <p className="text-sm text-muted-foreground">Image could not be loaded</p>;
    }

    return (
      <>
        {loading && <Loader />}
        <img
          src={content.signedUrl}
          alt={content.name}
          className={`absolute inset-0 m-auto max-h-full max-w-full object-contain ${loading ? 'invisible' : ''}`}
          onLoad={() => setLoadedSrc(content.signedUrl)}
          onError={() => setFailedSrc(content.signedUrl)}
        />
        {fullscreen && (
          <Lightbox onClose={onCloseFullscreen}>
            <img
              src={content.signedUrl}
              alt={content.name}
              className="max-h-full max-w-full object-contain"
            />
          </Lightbox>
        )}
      </>
    );
  }

  // ── PDF ────────────────────────────────────────────────────────────
  if (content.type === 'doc' && fileExt === 'pdf') {
    if (content.size > 20_000_000) {
      return <p className="text-sm text-muted-foreground">PDF is too large to preview (over 20 MB)</p>;
    }

    const thumbnailUrl = getPdfThumbnail(content);

    return (
      <>
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt="PDF preview"
            className="absolute inset-0 m-auto max-h-full max-w-full object-contain"
          />
        ) : (
          <iframe
            src={`${content.signedUrl}#toolbar=0`}
            title={content.name}
            className="h-full w-full border-none"
          />
        )}
        {fullscreen && (
          <Lightbox onClose={onCloseFullscreen}>
            <iframe
              src={`${content.signedUrl}#toolbar=0`}
              title={content.name}
              className="h-full w-full rounded border-none"
            />
          </Lightbox>
        )}
      </>
    );
  }

  // Other documents (txt, css, js, …) intentionally have no iframe preview:
  // the signed S3 URL is served as an attachment, so an iframe auto-downloads.
  return <p className="text-sm text-muted-foreground">.{fileExt} files can't be previewed</p>;
}

// ── Helpers ──────────────────────────────────────────────────────────

function Loader() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" />
    </div>
  );
}

function getPdfThumbnail(content: ResourceContent): string | undefined {
  if (content.previewImages?.length) return content.previewImages[0];
  if (content.snapshotUrl) return content.snapshotUrl;
  return undefined;
}
